/**
 * Late Fee Config Section Component
 * Allows landlord to configure late fee settings
 */

import { useState, useEffect } from 'react';
import { type PaymentConfig, type UpdatePaymentConfigInput, type LateFeeType } from '../../lib/api';
import { HelpBlock } from '../ui/HelpTooltip';

interface LateFeeConfigSectionProps {
  paymentConfig: PaymentConfig | null;
  onUpdate: (data: UpdatePaymentConfigInput) => Promise<boolean>;
}

export function LateFeeConfigSection({ paymentConfig, onUpdate }: LateFeeConfigSectionProps) {
  const [lateFeeType, setLateFeeType] = useState<LateFeeType | null>(paymentConfig?.lateFeeType ?? null);
  const [lateFeeAmount, setLateFeeAmount] = useState<string>(paymentConfig?.lateFeeAmount != null ? (paymentConfig.lateFeeAmount / 100).toString() : '');
  const [lateFeeMaxAmount, setLateFeeMaxAmount] = useState<string>(paymentConfig?.lateFeeMaxAmount != null ? (paymentConfig.lateFeeMaxAmount / 100).toString() : '');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (paymentConfig) {
      setLateFeeType(paymentConfig.lateFeeType);
      setLateFeeAmount(paymentConfig.lateFeeAmount != null ? (paymentConfig.lateFeeAmount / 100).toString() : '');
      setLateFeeMaxAmount(paymentConfig.lateFeeMaxAmount != null ? (paymentConfig.lateFeeMaxAmount / 100).toString() : '');
    }
  }, [paymentConfig]);

  // Clear amount fields when null (no late fee) is selected
  useEffect(() => {
    if (lateFeeType === null) {
      setLateFeeAmount('');
      setLateFeeMaxAmount('');
    }
  }, [lateFeeType]);

  useEffect(() => {
    if (lateFeeType === null) {
      const changed = lateFeeType !== (paymentConfig?.lateFeeType ?? null);
      setHasChanges(changed);
    } else {
      const changed =
        lateFeeType !== (paymentConfig?.lateFeeType ?? null) ||
        lateFeeAmount !== (paymentConfig?.lateFeeAmount != null ? (paymentConfig.lateFeeAmount / 100).toString() : '') ||
        (lateFeeType === 'daily' && lateFeeMaxAmount !== (paymentConfig?.lateFeeMaxAmount != null ? (paymentConfig.lateFeeMaxAmount / 100).toString() : ''));
      setHasChanges(changed);
    }
  }, [lateFeeType, lateFeeAmount, lateFeeMaxAmount, paymentConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: UpdatePaymentConfigInput = {
      lateFeeType,
      lateFeeAmount: lateFeeType === null ? null : Math.round(parseFloat(lateFeeAmount) * 100) || null,
      lateFeeMaxAmount: lateFeeType === 'daily' ? Math.round(parseFloat(lateFeeMaxAmount) * 100) || null : null,
    };

    // Guard the in-flight window (handleSubmit awaits the PUT before hiding the button); only clear
    // the unsaved-changes affordance once the save actually persists.
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const ok = await onUpdate(data);
      if (ok) setHasChanges(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const requiresAmount = lateFeeType !== null;
  const requiresMaxAmount = lateFeeType === 'daily';

  return (
    <form onSubmit={handleSubmit} data-testid="late-fee-config-form">
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold m-0">
          Late Fee Settings
        </h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">
          Configure late fees for overdue payments
        </p>
        <div className="mb-5">
          <HelpBlock
            summary="Late fees are charged when a tenant's payment is overdue beyond the grace period. Choose a fee type that matches your lease agreement and local regulations."
            details={"Fee types:\n\u2022 Flat Fee \u2014 A one-time fixed amount (e.g., $50). Charged once after the grace period expires.\n\u2022 Percentage \u2014 A percentage of the original charge (e.g., 5% of rent). Charged once after the grace period.\n\u2022 Daily Fee \u2014 A per-day charge that accrues until paid, up to a maximum cap you set.\n\nHow late fees are applied depends on your payment method:\n\u2022 ACH leases \u2014 Late fees are assessed automatically by the system.\n\u2022 External payment leases \u2014 Late fees require your approval on the External Payments page, since the system can't be 100% certain whether an external payment was received.\n\nImportant: Many states cap late fees or require specific fee structures. Check your local landlord-tenant laws to ensure compliance."}
          />
        </div>

        <div className="grid gap-4">
          {/* Late Fee Type */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="late-fee-type"
              className="text-sm font-medium text-foreground"
            >
              Late Fee Type
            </label>
            <select
              id="late-fee-type"
              data-testid="late-fee-type-input"
              value={lateFeeType ?? ''}
              onChange={(e) => setLateFeeType(e.target.value === '' ? null : e.target.value as LateFeeType)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
            >
              <option value="">No Late Fee</option>
              <option value="flat">Flat Fee</option>
              <option value="percentage">Percentage of Rent</option>
              <option value="daily">Daily Fee (with cap)</option>
            </select>
          </div>

          {/* Late Fee Amount */}
          {requiresAmount && (
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="late-fee-amount"
                className="text-sm font-medium text-foreground"
              >
                {lateFeeType === 'flat' && 'Late Fee Amount ($)'}
                {lateFeeType === 'percentage' && 'Late Fee Percentage (%)'}
                {lateFeeType === 'daily' && 'Daily Late Fee ($)'}
              </label>
              <div className="relative">
                {(lateFeeType === 'flat' || lateFeeType === 'daily') && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                )}
                <input
                  id="late-fee-amount"
                  data-testid="late-fee-amount-input"
                  type="number"
                  min="0"
                  step={lateFeeType === 'percentage' ? '0.01' : '1'}
                  max={lateFeeType === 'percentage' ? '100' : undefined}
                  value={lateFeeAmount}
                  onChange={(e) => setLateFeeAmount(e.target.value)}
                  required={requiresAmount}
                  aria-required={requiresAmount}
                  className={`w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px] ${
                    lateFeeType === 'flat' || lateFeeType === 'daily' ? 'pl-7' : ''
                  } ${lateFeeType === 'percentage' ? 'pr-7' : ''}`}
                />
                {lateFeeType === 'percentage' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    %
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 m-0">
                {lateFeeType === 'flat' && 'Fixed amount charged after grace period'}
                {lateFeeType === 'percentage' && 'Percentage of the original charge amount'}
                {lateFeeType === 'daily' && 'Amount charged per day until payment is received'}
              </p>
            </div>
          )}

          {/* Late Fee Max Amount (for daily only) */}
          {requiresMaxAmount && (
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="late-fee-max-amount"
                className="text-sm font-medium text-foreground"
              >
                Maximum Late Fee ($)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  $
                </span>
                <input
                  id="late-fee-max-amount"
                  data-testid="late-fee-max-amount-input"
                  type="number"
                  min="0"
                  step="1"
                  value={lateFeeMaxAmount}
                  onChange={(e) => setLateFeeMaxAmount(e.target.value)}
                  required={requiresMaxAmount}
                  aria-required={requiresMaxAmount}
                  className="w-full px-3 py-2 pl-7 text-sm border border-input rounded-md min-h-[44px]"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 m-0">
                Cap on the total late fee amount
              </p>
            </div>
          )}

          {/* Info Box */}
          {lateFeeType !== null && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
              <p className="text-sm text-primary m-0">
                <strong>How it works:</strong>
                {lateFeeType === 'flat' && ` A one-time fee of $${lateFeeAmount || '0'} will be charged after the grace period.`}
                {lateFeeType === 'percentage' && ` A fee of ${lateFeeAmount || '0'}% of the charge amount will be applied after the grace period.`}
                {lateFeeType === 'daily' && ` A daily fee of $${lateFeeAmount || '0'} will be charged each day after the grace period, up to a maximum of $${lateFeeMaxAmount || '0'}.`}
              </p>
            </div>
          )}
        </div>

        {(hasChanges || (paymentConfig != null && paymentConfig.lateFeesSetAt == null)) && (
          <button
            type="submit"
            data-testid="save-late-fee-submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="mt-5 px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 min-h-[44px] disabled:opacity-60 disabled:cursor-default"
          >
            {isSubmitting ? 'Saving…' : 'Save Late Fee Settings'}
          </button>
        )}
      </div>
    </form>
  );
}
